/**
 * Preorder Bundle Guard
 * ---------------------
 * A product tagged "preorder" can only stay in the cart as part of a bundle.
 * A preorder line is valid only if it carries a bundle discount (e.g. "Bundle #2").
 *
 * - If the customer removes the other bundle product, the bundle discount disappears
 *   and the preorder line is removed automatically.
 * - If the preorder product is added on its own (or an extra full-price unit gets
 *   split into a separate line), that line is removed too.
 */
import { CartUpdateEvent } from '@theme/events';

const PREORDER_TAG = 'preorder'; // product tag (compared case-insensitively)
const BUNDLE_KEYWORDS = ['bundle']; // words in the bundle discount title
const DEBOUNCE_MS = 800; // wait for multi-step bundle adds to finish

const ROOT = window.Shopify?.routes?.root || '/';
const CART_URL_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
const originalFetch = window.fetch.bind(window);
const tagCache = new Map();

let timer = null;
let running = false;
let pending = false;

/** @param {string} handle */
function isPreorderProduct(handle) {
  if (!tagCache.has(handle)) {
    const request = originalFetch(`${ROOT}products/${handle}.js`)
      .then((response) => (response.ok ? response.json() : null))
      .then(
        (product) =>
          !!product && (product.tags || []).some((tag) => String(tag).toLowerCase() === PREORDER_TAG)
      )
      .catch(() => false);
    tagCache.set(handle, request);
  }
  return tagCache.get(handle);
}

/** @param {any} item */
function hasBundleDiscount(item) {
  const titles = [
    ...(item.line_level_discount_allocations || []).map((a) => a.discount_application?.title),
    ...(item.discounts || []).map((d) => d.title),
  ]
    .filter(Boolean)
    .map((title) => String(title).toLowerCase());

  return titles.some((title) => BUNDLE_KEYWORDS.some((keyword) => title.includes(keyword)));
}

function showNotice() {
  const isArabic = (document.documentElement.lang || '').toLowerCase().startsWith('ar');
  const text = isArabic
    ? 'منتج الطلب المسبق متاح فقط ضمن العرض (الباندل)، لذلك تم حذفه من السلة.'
    : 'This pre-order item is only available as part of the bundle, so it was removed from your cart.';

  document.querySelector('.preorder-guard-notice')?.remove();

  const notice = document.createElement('div');
  notice.className = 'preorder-guard-notice';
  notice.setAttribute('role', 'status');
  notice.textContent = text;
  Object.assign(notice.style, {
    position: 'fixed',
    insetInline: '16px',
    bottom: '24px',
    margin: '0 auto',
    maxWidth: '420px',
    padding: '14px 18px',
    background: '#1f2937',
    color: '#ffffff',
    borderRadius: '10px',
    fontSize: '14px',
    lineHeight: '1.5',
    textAlign: 'center',
    zIndex: '2147483647',
    boxShadow: '0 8px 24px rgba(0,0,0,.25)',
    direction: isArabic ? 'rtl' : 'ltr',
  });

  document.body.appendChild(notice);
  setTimeout(() => notice.remove(), 5000);
}

async function enforce() {
  if (running) {
    pending = true;
    return;
  }
  running = true;

  try {
    const cart = await originalFetch(`${ROOT}cart.js`, { cache: 'no-store' }).then((r) => r.json());

    const invalidKeys = [];
    for (const item of cart.items || []) {
      if (!(await isPreorderProduct(item.handle))) continue;
      if (!hasBundleDiscount(item)) invalidKeys.push(item.key);
    }

    if (invalidKeys.length === 0) return;

    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component[data-section-id]')].map(
          (el) => /** @type {HTMLElement} */ (el).dataset.sectionId
        )
      ),
    ];

    /** @type {Record<string, any>} */
    const body = { updates: Object.fromEntries(invalidKeys.map((key) => [key, 0])) };
    if (sectionIds.length) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }

    const updatedCart = await originalFetch(`${ROOT}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json());

    // Refresh cart drawer / cart page / cart bubble
    document.dispatchEvent(
      new CartUpdateEvent(updatedCart, 'preorder-bundle-guard', {
        itemCount: updatedCart.item_count,
        source: 'preorder-bundle-guard',
        sections: updatedCart.sections,
      })
    );

    showNotice();
  } catch (error) {
    console.error('[preorder-bundle-guard]', error);
  } finally {
    running = false;
    if (pending) {
      pending = false;
      schedule();
    }
  }
}

function schedule() {
  clearTimeout(timer);
  timer = setTimeout(enforce, DEBOUNCE_MS);
}

// Watch every cart request made with fetch (theme + bundle app)
window.fetch = function (input, init) {
  const url = String(input?.url ?? input ?? '');
  const request = originalFetch(input, init);
  if (CART_URL_RE.test(url)) request.then(schedule, () => {});
  return request;
};

// Watch cart requests made with XMLHttpRequest (older apps / jQuery)
const originalOpen = XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open = function (method, url, ...rest) {
  if (CART_URL_RE.test(String(url))) this.addEventListener('loadend', schedule);
  return originalOpen.call(this, method, url, ...rest);
};

// Check once on page load (covers non-AJAX adds and old carts)
schedule();
